import { useQuery } from '@tanstack/react-query'
import type { IrrigationSiteDto } from '@lib/dto/irrigation.dto'
import {
  UNPRICEABLE_REASON_MESSAGES,
  type UnpriceableReason,
} from '@lib/helpers/irrigation-pricing'
import { formatMoney, parseMoney } from '@lib/helpers/money'
import { irrigationService } from '@/services/IrrigationService'
import { irrigationKeys } from '@/hooks/queryKeys'

/** Changes whenever the server's copy of the site's repairs does. */
function repairsRevision(site: IrrigationSiteDto): string {
  return site.problems
    .map((problem) => `${problem.id}@${problem.updatedAt}`)
    .join(',')
}

function money(value: string): string {
  return formatMoney(parseMoney(value) ?? 0)
}

/** "2 have no labor or parts recorded; 1 uses a part…" */
function describeUnpriceable(reasons: readonly UnpriceableReason[]): string {
  const counts = new Map<UnpriceableReason, number>()
  for (const reason of reasons) {
    counts.set(reason, (counts.get(reason) ?? 0) + 1)
  }
  return [...counts]
    .map(([reason, count]) => {
      const message = UNPRICEABLE_REASON_MESSAGES[reason]
      return count === 1
        ? `1 ${message}`
        : `${String(count)} ${message.replace(/^has /, 'have ').replace(/^uses /, 'use ')}`
    })
    .join('; ')
}

/**
 * What the site's open repairs come to, under the survey map.
 *
 * Priced by the server from the copy of Housecall Pro, so it reflects what
 * has synced, and refreshes when a sync changes the repairs. Its real job is
 * the second line: a repair that cannot be priced stops a proposal being
 * sent, and the cheapest moment to fix one is while the technician is still
 * standing over it. Nothing is shown with no open repairs, or offline.
 */
export function RepairEstimate({
  site,
  enabled = true,
}: {
  site: IrrigationSiteDto
  /** False until the visit it prices has reached the server. */
  enabled?: boolean
}) {
  const estimate = useQuery({
    enabled,
    queryKey: irrigationKeys.estimate(
      site.siteId,
      `${site.visitId ?? 'current'}:${repairsRevision(site)}`,
    ),
    queryFn: () => irrigationService.estimate(site.siteId, site.visitId),
    retry: false,
  })
  const data = estimate.data
  if (data === undefined) return null
  if (data.pricedRepairCount === 0 && data.unpriceable.length === 0) {
    return null
  }

  return (
    <div className="space-y-0.5 text-xs">
      {data.pricedRepairCount === 0 ? null : (
        <p className="text-secondary-text">
          {data.pricedRepairCount === 1
            ? '1 repair'
            : `${String(data.pricedRepairCount)} repairs`}{' '}
          · {money(data.amount)} before tax
          {data.driveCharge === null
            ? null
            : ` · includes ${money(data.driveCharge.price)} to drive to ${data.driveCharge.town}`}
        </p>
      )}
      {data.unpriceable.length === 0 ? null : (
        <p className="text-warning">
          Cannot be priced yet:{' '}
          {describeUnpriceable(data.unpriceable.map((entry) => entry.reason))}.
        </p>
      )}
    </div>
  )
}
